import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import ImageColumGrid from '../components/common/ImageColumGrid';
import { AppContext } from '../contexts/AppContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import Snackbar from '@mui/material/Snackbar';
import StravaConnect from '../components/comparison/StravaConnect';
import StravaActivitySelect from '../components/comparison/StravaActivitySelect';
import CompareStep3Review from '../components/comparison/CompareStep3Review';
import posthog from 'posthog-js';
import useRestricted from '../hooks/useRestricted';
import { Permission } from '../services/UserService';
import LoadingButton from '@mui/lab/LoadingButton';

export type CompareActivityState = "connect" | "select" | "review" | "complete";

const CompareActivity: React.FC = observer(() => {
  const permission = useRestricted();

  const intl = useIntl();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (permission === Permission.free) {
      navigate('/');
    }
  }, [permission, navigate]);
  const { userId, planId } = useParams();
  const [searchParams] = useSearchParams();
  const stravaCode = searchParams.get('code');
  const stravaError = searchParams.get('error');

  const { userService, plansService, stravaService, comparisonsService } = React.useContext(AppContext);

  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [selectedActivityId, setSelectedActivityId] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const loadPlan = async () => {
      if (userId && planId && userService.user?.id) {
        try {
          await plansService.loadPlan(userId, planId);
        } catch (error) {
          console.error("Error loading plan:", error);
          setError("error.planNotFound");
        }
      }
    };

    if (!plansService.plan && userService.user?.id) {
      loadPlan();
    }
  }, [userId, planId, userService.user?.id, plansService]);

  React.useEffect(() => {
    async function handleCallback() {
      setError(null);

      if (!stravaCode && !stravaError) return;

      if (stravaError) {
        setError("error.stravaAuthDeniedOrCancelled");
        return;
      }

      if (stravaCode) {
        setIsLoading(true);

        setTimeout(async () => {
          try {
            const success = await stravaService.handleCallback(stravaCode);
            if (success) {
              setError(null);
            } else {
              setError("error.stravaFailedConnect");
            }
          } catch (err) {
            setError("error.stravaUnexpectedError");
          } finally {
            setIsLoading(false);
          }
        }, 1000);
      }
    }

    if (!stravaService.isConnected && (stravaCode || stravaError)) {
      handleCallback();
    }
  }, [stravaCode, stravaError, stravaService, plansService.plan]);

  React.useEffect(() => {
    if (stravaService.isConnected && !isLoading) {

      if (activeStep === 0) {
        setActiveStep(1);
      }

      if (plansService.plan?.date) {
        const startOfDay = new Date(plansService.plan.date);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(plansService.plan.date);
        endOfDay.setHours(23, 59, 59, 999);

        stravaService.getActivitiesByDateRange(startOfDay, endOfDay);
      }
    }
  }, [stravaService.isConnected, activeStep, plansService.plan, isLoading, stravaService]);

  const handleBack = async () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    } else {
      await plansService.clearPlan();
      handleCancel();
    }
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleCancel = () => {
    if (userId && planId) {
      navigate(`/plans/${userId}/${planId}`);
    } else {
      navigate('/plans');
    }
  };

  const handleSelectActivity = async (activityId: number) => {
    setSelectedActivityId(activityId);
    await stravaService.getActivityStreams(activityId);
  };

  const handleSubmit = async () => {
    if (!selectedActivityId || !userId || !planId || !userService.user?.id || !plansService.plan) {
      setError('error.missingRequiredData');
      return;
    }

    setIsLoading(true);

    try {
      if (plansService.plan.comparisonId) {
        await comparisonsService.deleteComparison(plansService.plan.comparisonId, userService.user.id);
      }

      const activity = stravaService.activities.find(a => a.id === selectedActivityId);
      if (!activity) {
        throw new Error('Selected activity not found');
      }

      let streams = null;
      try {
        streams = await stravaService.getActivityStreams(selectedActivityId);
      } catch (err) {
        console.error("Error fetching activity streams:", err);
      }

      const comparison = await comparisonsService.saveComparison(
        planId,
        userService.user.id,
        activity,
        undefined, // No previous comparison ID
        streams || undefined
      );

      await plansService.setPlanData({
        ...plansService.plan,
        comparisonId: comparison?.id
      });

      posthog.capture('comparison_created', {
        plan_id: planId,
        comparison_id: comparison?.id,
        activity_id: selectedActivityId
      });

      await plansService.clearPlan();
      navigate(`/plans/${userId}/${planId}`);

    } catch (error) {
      console.error("Error saving comparison:", error);
      setError('error.savingComparison');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    intl.formatMessage({ id: 'comparison.steps.connect' }),
    intl.formatMessage({ id: 'comparison.steps.select' }),
    intl.formatMessage({ id: 'comparison.steps.review' }),
  ];

  const getPrimaryButtonProps = () => {
    if (activeStep === steps.length - 1) {
      return {
        onClick: handleSubmit,
        disabled: !selectedActivityId || isLoading,
        label: intl.formatMessage({ id: 'action.confirm' })
      };
    } else {
      return {
        onClick: handleNext,
        disabled:
          (activeStep === 0 && !stravaService.isConnected) ||
          (activeStep === 1 && !selectedActivityId) ||
          isLoading,
        label: intl.formatMessage({ id: 'nav.next' })
      };
    }
  };

  const primaryButtonProps = getPrimaryButtonProps();

  if (!plansService.plan && !error) {
    return (
      <ImageColumGrid image={`${process.env.PUBLIC_URL}/images/auth-background.jpg`}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
          <CircularProgress />
        </Box>
      </ImageColumGrid>
    );
  }

  const mainComponent = <Box
    sx={{
      my: 8,
      mx: 4,
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
    }}
  >

    <Button startIcon={<ArrowBackIcon />} sx={{ alignSelf: "flex-start" }}
      onClick={handleBack}
      color="inherit"
    >
      {intl.formatMessage({ id: 'action.back' })}
    </Button>

    <Avatar sx={{ marginX: "auto", marginY: 1, bgcolor: 'secondary.main' }}>
      <CompareArrowsIcon />
    </Avatar>
    <Typography component="h1" variant="h5" gutterBottom>
      {intl.formatMessage({ id: 'comparison.title' })}
    </Typography>

    {plansService.plan && (
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {plansService.plan.name}
      </Typography>
    )}

    {activeStep === 0 && (
      <StravaConnect
        isLoading={isLoading}
        error={error}
        onConnect={() => stravaService.connect()}
      />
    )}

    {activeStep === 1 && (
      <StravaActivitySelect
        activities={stravaService.activities}
        isLoading={stravaService.isLoading || isLoading}
        selectedActivityId={selectedActivityId}
        onSelectActivity={handleSelectActivity}
        date={plansService.plan?.date}
        error={error}
      />
    )}

    {activeStep === 2 && selectedActivityId && (
      <CompareStep3Review
        activity={stravaService.activities.find(a => a.id === selectedActivityId)}
        plan={plansService.plan || undefined}
      />
    )}

    <LoadingButton
      loading={isLoading}
      fullWidth
      variant="contained"
      onClick={primaryButtonProps.onClick}
      disabled={primaryButtonProps.disabled}
      sx={{ my: 1 }}
    >
      {primaryButtonProps.label}
    </LoadingButton>
  </Box>


  return (
    <>
      {
        activeStep > 1 ? mainComponent :
          <ImageColumGrid image={`${process.env.PUBLIC_URL}/images/auth-background.jpg`}>
            {mainComponent}
          </ImageColumGrid>
      }
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error ? intl.formatMessage({ id: error }) : ''}
      />
    </>
  );
});

export default CompareActivity;